import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Play, CheckCircle2, Trophy, Flag, Landmark, Map, ScrollText, Sparkles, Rocket, Clapperboard, Shield, Gamepad2, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import Countdown, { formatClock } from "@/components/Countdown";
import { toast } from "@/components/ui/sonner";

const ICONS = { flag: Flag, landmark: Landmark, map: Map, scroll: ScrollText, sparkles: Sparkles, trophy: Trophy, rocket: Rocket, clapperboard: Clapperboard, shield: Shield, "gamepad-2": Gamepad2 };

export default function Dashboard() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/quiz/dashboard");
      setData(data);
      setErr("");
    } catch (e) {
      setErr(apiError(e));
    }
  };

  useEffect(() => {
    if (user === null) navigate("/auth");
    else if (user && !user.profile_complete) navigate("/auth");
    else if (user) load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <div data-testid="dashboard-error" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-6 font-semibold">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="max-w-7xl mx-auto px-5 py-24 text-center text-slate-400 font-semibold">Loading your dashboard…</div>;
  }

  const nextLocked = data.categories.find((c) => c.state === "locked");
  const available = data.categories.filter((c) => c.state === "available" || c.state === "in_progress");

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="participant-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase">Competitor Dashboard</div>
          <h1 className="text-3xl md:text-4xl font-black font-heading text-slate-900 tracking-tighter mt-2">
            Namaste, <span className="text-gradient-tricolor">{data.user.display_name}</span>
          </h1>
        </div>
        <Countdown targetIso={data.competition.start_at} label="Quiz Starts In" liveLabel="QUIZ IS LIVE 🇮🇳" compact />
      </div>

      {data.competition.status === "paused" && (
        <div data-testid="competition-paused" className="mt-6 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-5 py-4 font-semibold text-sm">
          Competition temporarily paused by administrator.
        </div>
      )}

      {/* KPI strip */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Score", value: data.score, color: "#FF9933", testid: "dash-score" },
          { label: "Current Rank", value: data.rank ? `#${data.rank}` : "—", color: "#003366", testid: "dash-rank" },
          { label: "Completed", value: `${data.completed}/${data.total_categories}`, color: "#138808", testid: "dash-completed" },
          { label: "Unlocked Now", value: available.length, color: "#FF9933", testid: "dash-unlocked" },
        ].map((k) => (
          <div key={k.label} data-testid={k.testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="font-timer text-3xl font-bold" style={{ color: k.color }}>{k.value}</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      {nextLocked && (
        <div className="mt-8 bg-[#003366]/5 border border-[#003366]/15 rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm font-semibold text-[#003366]">Next unlock: <b>{nextLocked.name}</b></div>
          <Countdown targetIso={nextLocked.unlock_at} label={null} liveLabel="UNLOCKED" compact />
        </div>
      )}

      {/* Category grid */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data.categories.map((c, i) => {
          const Icon = ICONS[c.icon] || Flag;
          const locked = c.state === "locked";
          const done = c.state === "completed";
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`category-card-${c.order}`}
              className={`relative bg-white border rounded-xl p-5 transition-[transform,box-shadow] duration-300 ${locked ? "border-slate-200 opacity-70" : done ? "border-[#FF9933]/40" : "border-[#138808]/40 hover:-translate-y-1 hover:shadow-lg"}`}>
              <div className="flex items-start justify-between">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${locked ? "bg-slate-100 text-slate-400" : "text-white"}`} style={!locked ? { background: c.color } : {}}>
                  {locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                {done ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#E6892E] bg-[#FF9933]/10 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Completed</span>
                ) : locked ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-1 rounded-full">Locked</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#138808] bg-[#138808]/10 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-[#138808] animate-pulse" /> Available</span>
                )}
              </div>
              <div className="mt-4 font-heading font-bold text-slate-900">{String(c.order).padStart(2, "0")}. {c.name}</div>
              <div className="text-xs text-slate-400 mt-1 line-clamp-1">{c.description}</div>
              <div className="mt-4 pt-3 border-t border-slate-100">
                {locked && <div className="text-xs font-semibold text-slate-400">Unlocks at <b className="text-slate-600">{formatClock(c.unlock_at)} IST</b></div>}
                {done && c.attempt && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-[#E6892E] font-timer text-base">{c.attempt.score} pts</span>
                    <span className="text-slate-400">{c.attempt.correct}/{c.question_count} correct • {(c.attempt.time_ms / 1000).toFixed(0)}s</span>
                  </div>
                )}
                {(c.state === "available" || c.state === "in_progress") && (
                  <button onClick={() => navigate(`/quiz/${c.id}`)} data-testid={`start-category-${c.order}`}
                    className="w-full mt-1 py-2.5 rounded-lg bg-[#138808] text-white text-sm font-bold hover:bg-[#0f6d06] transition-colors inline-flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" /> {c.state === "in_progress" ? "Resume Category" : "Start Category"}
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-12 flex flex-wrap gap-4">
        <Link to="/leaderboard" data-testid="dash-leaderboard-link" className="px-6 py-3 rounded-lg bg-[#003366] text-white text-sm font-bold hover:bg-[#00284f] transition-colors inline-flex items-center gap-2"><Trophy className="w-4 h-4" /> Leaderboard</Link>
        <Link to="/results" data-testid="dash-results-link" className="px-6 py-3 rounded-lg border-2 border-[#FF9933] text-[#E6892E] text-sm font-bold hover:bg-[#FF9933] hover:text-white transition-colors">My Results</Link>
        <button onClick={load} data-testid="dash-refresh-btn" className="px-4 py-3 rounded-lg border border-slate-200 text-slate-500 text-sm font-semibold hover:border-slate-400 transition-colors inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
    </div>
  );
}
